import { useMutation, useQuery } from '@tanstack/react-query'
import { Consumer, createConsumer, Subscription } from '@rails/actioncable'
import Cookies from 'js-cookie'
import { useEffect, useRef } from 'react'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { playNotificationSound } from '../notifications/playNotificationSound'
import type { AppNotification, NotificationAudience, NotificationCategory } from '../notifications/types'
import { queryClient } from '../store/provider/TanStackProvider'

type NotificationApiAttrs = {
  category: NotificationCategory
  title: string
  body: string
  action_url?: string | null
  audience: NotificationAudience
  read_at: string | null
  created_at: string
  unread?: boolean
  notifiable_type?: string | null
  notifiable_id?: string | number | null
}

export type MarkReadByNotifiableInput = {
  type: string
  id: string
}

type NotificationResource = {
  id: string
  type: string
  attributes: NotificationApiAttrs
}

type NotificationsIndexResponse = {
  data: NotificationResource[]
  meta?: { unread_count?: number }
}

type NotificationsQueryData = {
  items: AppNotification[]
  unreadCount: number
}

const mapNotification = (row: NotificationResource): AppNotification => ({
  id: row.id,
  category: row.attributes.category,
  title: row.attributes.title,
  body: row.attributes.body,
  actionUrl: row.attributes.action_url,
  audience: row.attributes.audience,
  readAt: row.attributes.read_at,
  createdAt: row.attributes.created_at,
  unread: row.attributes.unread ?? !row.attributes.read_at,
  notifiableType: row.attributes.notifiable_type ?? null,
  notifiableId:
    row.attributes.notifiable_id === null || row.attributes.notifiable_id === undefined
      ? null
      : String(row.attributes.notifiable_id)
})

const markNotifiableReadInCache = (
  data: NotificationsQueryData | undefined,
  type: string,
  id: string,
  readAt: string
) => {
  if (!data) return data
  const items = data.items.map(item =>
    item.notifiableType === type && item.notifiableId === id && !item.readAt ? { ...item, readAt, unread: false } : item
  )
  return {
    items,
    unreadCount: items.filter(i => i.unread).length
  }
}

const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const

let consumer: Consumer | null
let subscription: Subscription | null
let subscribersCount = 0

const createCableUrl = () => {
  const uid = Cookies.get('uid')
  const accessToken = Cookies.get('access-token')
  const client = Cookies.get('client')
  const apiBaseUrl = process.env.REACT_APP_API_URL

  if (!uid || !accessToken || !client || !apiBaseUrl) return null

  const wsBaseUrl = apiBaseUrl
    .trim()
    .replace(/\/+$/, '')
    .replace(/^http:/, 'ws:')
    .replace(/^https:/, 'wss:')
  return `${wsBaseUrl}/cable?uid=${encodeURIComponent(uid)}&access-token=${encodeURIComponent(
    accessToken
  )}&client=${encodeURIComponent(client)}`
}

const ensureNotificationsSubscription = () => {
  if (consumer && subscription) return

  const cableUrl = createCableUrl()
  if (!cableUrl) return

  try {
    consumer = createConsumer(cableUrl)
    subscription = consumer.subscriptions.create(
      { channel: 'NotificationsChannel' },
      {
        received() {
          playNotificationSound()
          queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
        }
      }
    )
  } catch (error) {
    console.error('Notifications ActionCable setup failed:', error)
  }
}

const cleanupNotificationsSubscription = () => {
  if (subscribersCount > 0) return

  subscription?.unsubscribe?.()
  consumer?.disconnect?.()
  subscription = null
  consumer = null
}

export const useNotificationsSubscription = (enabled = true) => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    subscribersCount += 1
    ensureNotificationsSubscription()

    return () => {
      subscribersCount = Math.max(0, subscribersCount - 1)
      cleanupNotificationsSubscription()
    }
  }, [enabled])
}

const markItemReadInCache = (data: NotificationsQueryData | undefined, id: string, readAt: string) => {
  if (!data) return data
  const items = data.items.map(item => (item.id === id && !item.readAt ? { ...item, readAt, unread: false } : item))
  return {
    items,
    unreadCount: items.filter(i => i.unread).length
  }
}

const markAllReadInCache = (data: NotificationsQueryData | undefined, readAt: string) => {
  if (!data) return data
  const items = data.items.map(item => (item.readAt ? item : { ...item, readAt, unread: false }))
  return { items, unreadCount: 0 }
}

export const useNotifications = (opts?: { audience?: NotificationAudience; enabled?: boolean }) => {
  const enabled = opts?.enabled !== false
  const audience = opts?.audience
  const queryKey = [...NOTIFICATIONS_QUERY_KEY, { audience }] as const

  useNotificationsSubscription(enabled)

  const { data, isLoading, isError } = useQuery({
    enabled,
    queryFn: async () => {
      const { data: res } = await AXIOS_INSTANCE.get<NotificationsIndexResponse>('notifications', {
        params: { 'page[size]': 30 }
      })
      const items = (res.data || []).map(mapNotification)
      const filtered = audience ? items.filter(i => i.audience === audience) : items
      return {
        items: filtered,
        unreadCount: filtered.filter(i => i.unread).length
      }
    },
    queryKey,
    refetchInterval: 60_000
  })

  // Poll / refetch fallback when ActionCable did not deliver (or was unavailable).
  const prevUnreadRef = useRef<number | null>(null)
  useEffect(() => {
    if (!enabled || data == null) return
    const prev = prevUnreadRef.current
    prevUnreadRef.current = data.unreadCount
    if (prev !== null && data.unreadCount > prev) {
      playNotificationSound()
    }
  }, [enabled, data])

  const { mutateAsync: markRead } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.patch(`notifications/${id}/mark_read`)
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const readAt = new Date().toISOString()
      queryClient.setQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY }, old =>
        markItemReadInCache(old, id, readAt)
      )
      return { previous }
    },
    onError: (_err, _id, context) => {
      context?.previous?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    }
  })

  const { mutateAsync: markAllRead } = useMutation({
    mutationFn: async () => {
      await AXIOS_INSTANCE.patch('notifications/mark_all_read')
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const readAt = new Date().toISOString()
      queryClient.setQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY }, old =>
        markAllReadInCache(old, readAt)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    }
  })

  const { mutateAsync: markReadByNotifiable } = useMutation({
    mutationFn: async ({ type, id }: MarkReadByNotifiableInput) => {
      await AXIOS_INSTANCE.patch('notifications/mark_read_by_notifiable', {
        notifiable_type: type,
        notifiable_id: id
      })
    },
    onMutate: async ({ type, id }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const readAt = new Date().toISOString()
      queryClient.setQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY }, old =>
        markNotifiableReadInCache(old, type, id, readAt)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    }
  })

  return {
    items: data?.items || [],
    unreadCount: data?.unreadCount || 0,
    isLoading,
    isError,
    markRead,
    markAllRead,
    markReadByNotifiable
  }
}
